import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UiBottomSheetFooterDirective } from './ui-bottom-sheet-footer.directive';

/** Distancia mínima arrastrada para descartar la hoja, en px. */
const DISMISS_DISTANCE = 96;
/** Fracción del alto de la hoja que también dispara el descarte. */
const DISMISS_RATIO = 0.25;
/** Velocidad de "flick" hacia abajo que descarta sin recorrer la distancia, en px/ms. */
const DISMISS_VELOCITY = 0.6;

@Component({
  selector: 'app-ui-bottom-sheet',
  templateUrl: './ui-bottom-sheet.component.html',
  styleUrls: ['./ui-bottom-sheet.component.css'],
  standalone: false,
})
export class UiBottomSheetComponent implements OnChanges, OnDestroy {
  private static lockCount = 0;

  @Input() open = false;
  @Input() title = '';
  @Input() closeAriaLabel = 'Cerrar';
  @Input() maxWidth = '700px';
  @Input() mutedBody = true;

  @Output() closed = new EventEmitter<void>();

  @ContentChild(UiBottomSheetFooterDirective)
  footer?: UiBottomSheetFooterDirective;

  @ViewChild('sheet') private sheetRef?: ElementRef<HTMLElement>;

  dragging = false;
  dragOffset = 0;

  private locked = false;
  private pointerId: number | null = null;
  private startY = 0;
  private startTime = 0;

  get backdropOpacity(): number | null {
    if (!this.dragOffset) return null;
    const height = this.sheetRef?.nativeElement.offsetHeight || 400;
    return Math.max(0.2, 1 - this.dragOffset / height);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      this.resetDrag();
      this.syncScrollLock(this.open);
    }
  }

  ngOnDestroy(): void {
    this.syncScrollLock(false);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) this.close();
  }

  close(): void {
    this.closed.emit();
  }

  onGrabStart(event: PointerEvent): void {
    if (!this.isDraggable()) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, input, select, textarea')) return;

    this.pointerId = event.pointerId;
    this.startY = event.clientY;
    this.startTime = event.timeStamp;
    this.dragging = true;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  onGrabMove(event: PointerEvent): void {
    if (!this.dragging || event.pointerId !== this.pointerId) return;
    const delta = event.clientY - this.startY;
    this.dragOffset = delta > 0 ? delta : delta / 6;
  }

  onGrabEnd(event: PointerEvent): void {
    if (!this.dragging || event.pointerId !== this.pointerId) return;

    const offset = this.dragOffset;
    const elapsed = Math.max(1, event.timeStamp - this.startTime);
    const height = this.sheetRef?.nativeElement.offsetHeight || 0;
    const draggedFarEnough = offset > Math.max(DISMISS_DISTANCE, height * DISMISS_RATIO);
    const flickedDown = offset / elapsed > DISMISS_VELOCITY && offset > 32;

    this.resetDrag();
    if (draggedFarEnough || flickedDown) this.close();
  }

  onGrabCancel(): void {
    this.resetDrag();
  }

  private isDraggable(): boolean {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  private resetDrag(): void {
    this.dragging = false;
    this.dragOffset = 0;
    this.pointerId = null;
  }

  private syncScrollLock(shouldLock: boolean): void {
    if (shouldLock === this.locked) return;
    this.locked = shouldLock;
    if (shouldLock) {
      UiBottomSheetComponent.lockCount += 1;
      document.body.style.overflow = 'hidden';
      return;
    }
    UiBottomSheetComponent.lockCount = Math.max(0, UiBottomSheetComponent.lockCount - 1);
    if (UiBottomSheetComponent.lockCount === 0) {
      document.body.style.overflow = '';
    }
  }
}
